import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import client, { getErrorMessage } from "@/api/client";
import { toast } from "sonner";
import { NoAccess } from "../equipment/shared/NoAccess";
import { VIEW_ROLES, fmtNum } from "./shared";

const CELL = {
  overdue: "bg-red-50 text-red-700 font-semibold",
  due_soon: "bg-amber-50 text-amber-800 font-semibold",
  ok: "text-slate-800",
};

function Cell({ c, id }) {
  if (c.status === "not_logged") return <span className="italic text-slate-400" data-testid={`${id}-text`}>Not logged</span>;
  if (c.status === "no_current_hmr") return <span className="italic text-slate-400" data-testid={`${id}-text`}>No current HMR</span>;
  const text = c.status === "overdue" ? `Overdue by ${fmtNum(c.overdue_by)} hrs` : `${fmtNum(c.remaining)} hrs left`;
  return (
    <div>
      <span data-testid={`${id}-text`}>{text}</span>
      <div className="text-[10px] font-normal text-slate-500">last @ {fmtNum(c.last_hmr)}</div>
    </div>
  );
}

export default function DueMatrixPage() {
  const { user } = useAuth();
  const allowed = VIEW_ROLES.includes(user?.role);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!allowed) return;
    client.get("/service-tracker/due-matrix")
      .then((r) => setData(r.data))
      .catch((e) => toast.error(getErrorMessage(e)))
      .finally(() => setLoading(false));
  }, [allowed]);

  if (!allowed) return <NoAccess />;

  return (
    <div className="space-y-6" data-testid="due-matrix-page">
      <div>
        <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>Due Matrix</h2>
        <p className="text-sm text-slate-500">
          Piling rigs against the service schedule. Red = overdue, amber = within {data?.due_soon_hours ?? 100} hrs of due. Calculated live from current HMR.
        </p>
      </div>

      <div className="rounded-lg border border-slate-200 bg-white overflow-auto max-h-[70vh]">
        {loading ? (
          <p className="p-8 text-center text-slate-400">Loading...</p>
        ) : !data || data.columns.length === 0 ? (
          <p className="p-8 text-center text-slate-400" data-testid="due-matrix-empty">No schedule lines yet — build the Service Schedule first.</p>
        ) : data.rows.length === 0 ? (
          <p className="p-8 text-center text-slate-400" data-testid="due-matrix-empty">No active Piling rigs.</p>
        ) : (
          <table className="text-sm border-collapse w-max min-w-full" data-testid="due-matrix-table">
            <thead>
              <tr>
                <th className="sticky left-0 top-0 z-30 bg-slate-100 border-b border-r border-slate-200 px-3 py-2 text-left min-w-[200px]">Equipment</th>
                <th className="sticky top-0 z-20 bg-slate-100 border-b border-slate-200 px-3 py-2 text-right min-w-[90px]">Current HMR</th>
                {data.columns.map((c) => (
                  <th key={c.line_id} className="sticky top-0 z-20 bg-slate-100 border-b border-l border-slate-200 px-3 py-2 text-left min-w-[150px]" data-testid={`due-matrix-col-${c.line_id}`}>
                    <div>{c.service_type}</div>
                    <div className="text-[11px] font-normal text-slate-500">{c.grade} · every {fmtNum(c.interval_hours)} hrs</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r) => {
                const hasOverdue = r.cells.some((c) => c.status === "overdue");
                return (
                <tr key={r.asset_id} className="border-b border-slate-100" data-testid={`due-matrix-row-${r.asset_id}`}>
                  <td className={`sticky left-0 z-10 bg-white border-r border-slate-200 px-3 py-2 font-medium ${hasOverdue ? "text-red-600" : ""}`} data-testid={`due-matrix-name-${r.asset_id}`} data-overdue={hasOverdue}>{r.asset_label}</td>
                  <td className="px-3 py-2 text-right font-mono" data-testid={`due-matrix-current-${r.asset_id}`}>{fmtNum(r.current_hmr)}</td>
                  {r.cells.map((c) => {
                    const id = `due-matrix-cell-${r.asset_id}-${c.line_id}`;
                    return (
                      <td key={c.line_id} className={`border-l border-slate-100 px-3 py-2 ${CELL[c.status] || ""}`} data-testid={id} data-status={c.status}>
                        <Cell c={c} id={id} />
                      </td>
                    );
                  })}
                </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
